/**
 * Prosumer Calculator - Form Hook
 *
 * Custom hook for managing the prosumer wizard form state
 * using Mantine's useForm with per-step validation and localStorage persistence.
 */

import { useForm } from '@mantine/form';
import { useState, useCallback, useEffect } from 'react';
import type {
  WizardStep,
  WizardNavigation,
  ProsumerFormValues,
  ProsumerFormErrors,
} from '../types';
import {
  WIZARD_STEPS,
  getStepIndex,
  DEFAULT_FORM_VALUES,
} from '../types';
import { getAvailableTariffsForOSD } from '../../../utils/osdMetadata';
import type { OSDCode } from '../../../utils/osdMetadata';

// =============================================================================
// LOCAL STORAGE KEY
// =============================================================================

const STORAGE_KEY = 'prosumer-form-state';

function loadFromStorage(): ProsumerFormValues | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as ProsumerFormValues;
      // Ensure tariffTypes always reflects the stored distributor's available tariffs.
      // This handles migration from older saved state where tariffTypes was [G11, G12, G12w].
      if (parsed.distributor) {
        parsed.tariffTypes = getAvailableTariffsForOSD(parsed.distributor as OSDCode);
      }
      return parsed;
    }
  } catch {
    // Ignore parse errors
  }
  return null;
}

function saveToStorage(values: ProsumerFormValues): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(values));
  } catch {
    // Ignore storage errors
  }
}

function clearStorage(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore
  }
}

// =============================================================================
// STEP VALIDATION
// =============================================================================

export function validateStep(values: ProsumerFormValues, step: WizardStep): ProsumerFormErrors {
  const errors: ProsumerFormErrors = {};

  switch (step) {
    case 'profile':
      if (!values.distributor) {
        errors.distributor = 'Wybierz dystrybutora';
      }
      break;

    case 'consumption':
      if (values.consumptionMethod === 'ANNUAL') {
        if (values.annualConsumptionKwh < 500 || values.annualConsumptionKwh > 50000) {
          errors.annualConsumptionKwh = 'Roczne zużycie musi być między 500 a 50 000 kWh';
        }
      }
      if (values.consumptionMethod === 'MONTHLY') {
        const sum = values.monthlyConsumptionKwh.reduce((a, b) => a + b, 0);
        if (sum < 500 || sum > 50000) {
          errors.monthlyConsumptionKwh = 'Suma miesięczna musi być między 500 a 50 000 kWh';
        }
      }
      if (values.consumptionMethod === 'CSV' && !values.csvData) {
        errors.csvData = 'Załaduj plik CSV z danymi o zużyciu';
      }
      if (values.consumptionMethod === 'IMPORTED' && !values.csvData) {
        errors.csvData = 'Załaduj zaimportowane dane godzinowe';
      }
      break;

    case 'devices':
      if (values.hasPV && values.pvConfig.capacityKwp < 1) {
        errors['pvConfig.capacityKwp'] = 'Moc instalacji PV musi wynosić co najmniej 1 kWp';
      }
      if (values.hasEV && values.evConfig.annualMileageKm < 1000) {
        errors['evConfig.annualMileageKm'] = 'Roczny przebieg musi wynosić co najmniej 1000 km';
      }
      if (values.hasHeatPump && values.heatPumpConfig.houseAreaM2 < 30) {
        errors['heatPumpConfig.houseAreaM2'] = 'Powierzchnia domu musi wynosić co najmniej 30 m²';
      }
      break;

    case 'results':
      // No validation on results
      break;
  }

  return errors;
}

// =============================================================================
// RETURN TYPE
// =============================================================================

export interface UseProsumerFormReturn {
  form: ReturnType<typeof useForm<ProsumerFormValues>>;
  currentStep: WizardStep;
  currentStepIndex: number;
  totalSteps: number;
  isOnResults: boolean;
  isStepValid: boolean;
  navigation: WizardNavigation;
  actions: {
    clearForm: () => void;
  };
}

// =============================================================================
// HOOK
// =============================================================================

export function useProsumerForm(): UseProsumerFormReturn {
  const savedState = loadFromStorage();
  const initialValues = savedState ?? { ...DEFAULT_FORM_VALUES };

  const [currentStep, setCurrentStep] = useState<WizardStep>('profile');

  const form = useForm<ProsumerFormValues>({
    mode: 'controlled',
    initialValues,
    validate: (values) => validateStep(values, currentStep),
  });

  // Auto-save to localStorage on form changes
  useEffect(() => {
    saveToStorage(form.values);
  }, [form.values]);

  // Derived state
  const currentStepIndex = getStepIndex(currentStep);
  const totalSteps = WIZARD_STEPS.length - 1; // Exclude results
  const isOnResults = currentStep === 'results';
  const isStepValid = Object.keys(validateStep(form.values, currentStep)).length === 0;

  // Navigation
  const goToNextStep = useCallback((): boolean => {
    const errors = validateStep(form.values, currentStep);
    form.setErrors(errors);

    if (Object.keys(errors).length > 0) {
      return false;
    }

    const nextIndex = currentStepIndex + 1;
    if (nextIndex < WIZARD_STEPS.length) {
      setCurrentStep(WIZARD_STEPS[nextIndex]);
      return true;
    }

    return false;
  }, [currentStep, currentStepIndex, form]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(WIZARD_STEPS[prevIndex]);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback((step: WizardStep) => {
    const targetIndex = getStepIndex(step);
    const currentIdx = getStepIndex(currentStep);

    // Can only go back or stay at current step
    if (targetIndex <= currentIdx) {
      setCurrentStep(step);
    }
  }, [currentStep]);

  const restart = useCallback(() => {
    form.reset();
    form.setValues({ ...DEFAULT_FORM_VALUES });
    setCurrentStep('profile');
    clearStorage();
  }, [form]);

  const clearForm = useCallback(() => {
    form.reset();
    form.setValues({ ...DEFAULT_FORM_VALUES });
    clearStorage();
  }, [form]);

  return {
    form,
    currentStep,
    currentStepIndex,
    totalSteps,
    isOnResults,
    isStepValid,
    navigation: {
      goToNextStep,
      goToPreviousStep,
      goToStep,
      restart,
    },
    actions: {
      clearForm,
    },
  };
}
